// The client half of the verifier boundary: handshake, id correlation, timeout, abort.
// SHIP-SPEC-2026-09-05 sections 3.1, 3.2 and 8.3.
//
// The worker is taken by INJECTION, never constructed here. `createVerifyWorker` is the only module
// that names `new Worker(new URL(...), import.meta.url)`, and no test may import it (U3 section 5.4:
// BABEL_ENV=test leaves `import.meta` in place and jest's vm.Script throws on it). Injecting the
// factory is what lets every test in this folder exercise the whole client against a fake worker.
//
// The client fails CLOSED. `available` is false when the factory returned null, when the handshake
// did not answer in time, when a reply carried the wrong protocol, or when the worker errored; the
// write lane then refuses with the named reason `verifier-unavailable` and issues no request of any
// kind (spec section 3.4 point 1, negative test E17).
import type {
  Fixture,
  RowCounts,
  Verdict,
  VerifiablePlan,
} from '../../dorian-shared/types/episodeScript';
import type {
  VerifierArtifactIdentity,
  VerifierCanonSet,
  VerifyWorkerFactory,
  VerifyWorkerLike,
  VerifyWorkerReply,
  VerifyWorkerRequest,
} from './workerProtocol';
import { BASIL_VERIFY_PROTOCOL, isVerifyWorkerReply } from './workerProtocol';

/** The handshake budget. A verifier that has not identified itself in this long does not exist. */
export const DEFAULT_HANDSHAKE_TIMEOUT_MS = 5000;

/** The verdict budget (spec section 3.2 rule 4). On expiry the worker is terminated and the apply aborts. */
export const DEFAULT_VERIFY_TIMEOUT_MS = 15000;

/** The message a caller sees when it asks an unavailable client for a verdict. */
export const VERIFIER_UNAVAILABLE_MESSAGE = 'verifier-unavailable';

export interface VerifyClientOptions {
  handshakeTimeoutMs?: number;
  verifyTimeoutMs?: number;
}

export interface VerifyClient {
  /** Identify the artifact. Idempotent, and safe to race: concurrent calls share one exchange. */
  handshake(): Promise<VerifierArtifactIdentity | null>;
  /** False until a handshake has succeeded; false again once the worker has failed. */
  readonly available: boolean;
  /** Who answered the handshake. Recorded in the audit entry before anything is written. */
  readonly artifact: VerifierArtifactIdentity | null;
  /** One verdict per chunk, from that chunk's own plan and its own before/after pair. */
  verify(before: Fixture, after: Fixture, plan: VerifiablePlan, actualCounts: RowCounts | null): Promise<Verdict>;
  /**
   * The verifier's own per-node canon lines and digests for one read (spec section 5.3).
   *
   * This is how the staleness baseline crosses the boundary. It rejects when the client is
   * unavailable and when the chunk does not know the request, so a caller can never mistake
   * "no canon" for "nothing drifted".
   */
  canon(fixture: Fixture): Promise<VerifierCanonSet>;
  /** Drop the worker and fail every request in flight. The client stays unavailable afterwards. */
  terminate(): void;
}

interface PendingExchange {
  timer: ReturnType<typeof setTimeout>;
  settle: (reply: VerifyWorkerReply) => void;
  fail: (error: Error) => void;
}

/** Structured-clone safety: the worker is handed serialized plain JSON, never a live writer object. */
function serialized<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * The verifier seam. `factory` returns a worker or null; `options` overrides the two budgets.
 *
 * The returned client owns at most one worker and at most one exchange per minted id. It ignores a
 * reply whose protocol is not the current one and a reply whose id it never sent.
 */
export function createVerifyClient(factory: VerifyWorkerFactory, options?: VerifyClientOptions): VerifyClient {
  const handshakeTimeoutMs = options?.handshakeTimeoutMs ?? DEFAULT_HANDSHAKE_TIMEOUT_MS;
  const verifyTimeoutMs = options?.verifyTimeoutMs ?? DEFAULT_VERIFY_TIMEOUT_MS;
  const pending = new Map<string, PendingExchange>();

  let worker: VerifyWorkerLike | null = null;
  let artifact: VerifierArtifactIdentity | null = null;
  let available = false;
  let started = false;
  let handshakeInFlight: Promise<VerifierArtifactIdentity | null> | null = null;
  let nextId = 0;

  function mintId(): string {
    nextId += 1;
    return `basil-${nextId}`;
  }

  function dropWorker(): void {
    const current = worker;

    worker = null;
    available = false;
    if (!current) return;
    current.onmessage = null;
    current.onerror = null;
    try {
      current.terminate();
    } catch (error) {
      // A worker that will not terminate is already gone as far as this client is concerned.
    }
  }

  function failEveryone(error: Error): void {
    const inFlight = [...pending.values()];

    pending.clear();
    inFlight.forEach((exchange) => {
      clearTimeout(exchange.timer);
      exchange.fail(error);
    });
  }

  function receive(data: unknown): void {
    if (!isVerifyWorkerReply(data)) return;
    const exchange = pending.get(data.id);

    if (!exchange) return;
    pending.delete(data.id);
    clearTimeout(exchange.timer);
    exchange.settle(data);
  }

  function send(request: VerifyWorkerRequest, timeoutMs: number): Promise<VerifyWorkerReply> {
    return new Promise<VerifyWorkerReply>((resolve, reject) => {
      const current = worker;

      if (!current) {
        reject(new Error(VERIFIER_UNAVAILABLE_MESSAGE));
        return;
      }
      const timer = setTimeout(() => {
        pending.delete(request.id);
        dropWorker();
        reject(new Error(`the verifier did not answer ${request.kind} within ${timeoutMs}ms`));
      }, timeoutMs);

      pending.set(request.id, { timer, settle: resolve, fail: reject });
      try {
        current.postMessage(request);
      } catch (error) {
        pending.delete(request.id);
        clearTimeout(timer);
        dropWorker();
        reject(new Error(VERIFIER_UNAVAILABLE_MESSAGE));
      }
    });
  }

  function runHandshake(): Promise<VerifierArtifactIdentity | null> {
    const created = factory();

    if (!created) {
      available = false;
      return Promise.resolve(null);
    }
    worker = created;
    worker.onmessage = (event: MessageEvent): void => {
      receive(event.data);
    };
    worker.onerror = (): void => {
      dropWorker();
      failEveryone(new Error(VERIFIER_UNAVAILABLE_MESSAGE));
    };
    return send({ id: mintId(), protocol: BASIL_VERIFY_PROTOCOL, kind: 'handshake' }, handshakeTimeoutMs)
      .then((reply) => {
        if (reply.kind !== 'handshake') {
          dropWorker();
          return null;
        }
        artifact = reply.artifact;
        available = true;
        return artifact;
      })
      .catch(() => {
        dropWorker();
        return null;
      });
  }

  function handshake(): Promise<VerifierArtifactIdentity | null> {
    if (handshakeInFlight) return handshakeInFlight;
    if (started) return Promise.resolve(available ? artifact : null);
    started = true;
    handshakeInFlight = runHandshake().then((result) => {
      handshakeInFlight = null;
      return result;
    });
    return handshakeInFlight;
  }

  function verify(
    before: Fixture,
    after: Fixture,
    plan: VerifiablePlan,
    actualCounts: RowCounts | null,
  ): Promise<Verdict> {
    return handshake().then((identity) => {
      if (!identity || !available) throw new Error(VERIFIER_UNAVAILABLE_MESSAGE);
      const request: VerifyWorkerRequest = {
        id: mintId(),
        protocol: BASIL_VERIFY_PROTOCOL,
        kind: 'verify',
        before: serialized(before),
        after: serialized(after),
        plan: serialized(plan),
        actualCounts: actualCounts === null ? null : serialized(actualCounts),
      };

      return send(request, verifyTimeoutMs).then((reply) => {
        if (reply.kind === 'verdict') {
          artifact = reply.artifact;
          return reply.verdict;
        }
        if (reply.kind === 'error') throw new Error(reply.message);
        throw new Error(`the verifier answered ${reply.kind} to a verify request`);
      });
    });
  }

  function canon(fixture: Fixture): Promise<VerifierCanonSet> {
    return handshake().then((identity) => {
      if (!identity || !available) throw new Error(VERIFIER_UNAVAILABLE_MESSAGE);
      const request: VerifyWorkerRequest = {
        id: mintId(),
        protocol: BASIL_VERIFY_PROTOCOL,
        kind: 'canon',
        fixture: serialized(fixture),
      };

      return send(request, verifyTimeoutMs).then((reply) => {
        if (reply.kind === 'canon') {
          artifact = reply.artifact;
          return reply.canon;
        }
        if (reply.kind === 'error') throw new Error(reply.message);
        throw new Error(`the verifier answered ${reply.kind} to a canon request`);
      });
    });
  }

  function terminate(): void {
    dropWorker();
    failEveryone(new Error(VERIFIER_UNAVAILABLE_MESSAGE));
    artifact = null;
    started = true;
    handshakeInFlight = null;
  }

  return {
    handshake,
    get available() {
      return available;
    },
    get artifact() {
      return artifact;
    },
    verify,
    canon,
    terminate,
  };
}
